import { Heart, Lock, Minus, Phone, PhoneCall, Plus, ShoppingCart, Zap } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";
import { formatPrice } from "@/lib/utils";
import type { Product, ProductVariant } from "@/types";

export function ProductActions({
  product,
  selectedVariant,
  compact = false
}: {
  product: Product;
  selectedVariant?: ProductVariant | null;
  compact?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((s) => s.addItem);
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const isWishlisted = useWishlistStore((s) => s.has(product.id));

  useEffect(() => setMounted(true), []);

  const safeIsWishlisted = mounted ? isWishlisted : false;

  const currentPrice = selectedVariant ? selectedVariant.price : product.price;
  const currentStock = selectedVariant?.stock !== undefined ? selectedVariant.stock : product.stock;
  const currentMrp = selectedVariant?.mrp ?? product.mrp;
  const currentDiscount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : product.discount;

  const maxQuantity = Math.max(1, Math.min(currentStock, 10));

  useEffect(() => {
    if (quantity > maxQuantity) {
      setQuantity(Math.max(1, maxQuantity));
    }
  }, [maxQuantity, quantity]);

  function addToCart() {
    addItem(product, quantity, selectedVariant);
    const variantLabel = selectedVariant?.title || selectedVariant?.color || selectedVariant?.id;
    const desc = selectedVariant
      ? `${quantity} × ${product.title} (${variantLabel})`
      : `${quantity} × ${product.title}`;
    toast.success("Added to cart", { description: desc });
  }

  // Commercial vendor products (with vendorId) are platform-sold e-commerce items with Add to Cart & Buy Now.
  // ONLY peer-to-peer customer community listings (without vendorId, having 'marketplace' tag or user phone post) show direct contact.
  const isVendorProduct = Boolean(product.vendorId);
  const isCustomerListing =
    !isVendorProduct &&
    Boolean(
      product.tags?.some((t) => t.toLowerCase() === "marketplace") ||
      (product.userId && !product.vendorId && product.sellerPhone)
    );

  return (
    <div className={compact ? "" : "rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900"}>
      {!compact && (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-[#073B70] dark:text-white">
              {formatPrice(currentPrice)}
            </span>
            {currentDiscount > 0 && (
              <span className="inline-flex items-center rounded-full bg-[#FF8A00] px-2 py-0.5 text-[11px] font-bold text-white">
                -{currentDiscount}%
              </span>
            )}
          </div>
          {currentMrp > currentPrice && (
            <p className="mt-0.5 text-xs text-slate-400">
              M.R.P.: <span className="line-through">{formatPrice(currentMrp)}</span>
            </p>
          )}

          <p
            className={`mt-3 text-sm font-bold ${
              isCustomerListing
                ? "text-emerald-600 dark:text-emerald-400"
                : currentStock > 5
                ? "text-[#00B7B0]"
                : currentStock > 0
                ? "text-[#FF8A00]"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {isCustomerListing
              ? "Community Device Listing · Available for direct pickup"
              : currentStock > 5
              ? "In stock · Ready to dispatch"
              : currentStock > 0
              ? `Only ${currentStock} left in stock - order soon`
              : "Currently out of stock"}
          </p>
        </>
      )}

      {!isCustomerListing && currentStock > 0 && (
        <div className="mt-3.5 flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Quantity:</span>
          <div className="inline-flex h-9 items-center rounded-lg border border-slate-300 bg-slate-50 overflow-hidden shadow-2xs dark:border-white/15 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              className="flex h-full w-9 items-center justify-center text-slate-700 transition hover:bg-slate-200 hover:text-slate-950 active:bg-slate-300 disabled:pointer-events-none disabled:opacity-30 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Decrease quantity"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="min-w-8 px-1 text-center text-sm font-bold text-[#073B70] select-none dark:text-white">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              disabled={quantity >= maxQuantity}
              className="flex h-full w-9 items-center justify-center text-slate-700 transition hover:bg-slate-200 hover:text-slate-950 active:bg-slate-300 disabled:pointer-events-none disabled:opacity-30 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Increase quantity"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {isCustomerListing && product.sellerPhone && (
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50/80 p-3.5 dark:border-emerald-500/30 dark:bg-emerald-950/30">
          <div className="flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300">
            <Phone className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wider">Contact Seller Directly</span>
          </div>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            Free Marketplace Listing. Connect directly with the seller for inspection or purchase:
          </p>
          <a
            href={`tel:${product.sellerPhone}`}
            className="mt-2.5 flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.99]"
          >
            <PhoneCall className="h-4 w-4" />
            Call {product.sellerPhone}
          </a>
        </div>
      )}

      <div className="mt-4 grid gap-2.5">
        {!isCustomerListing && (
          <>
            <button
              type="button"
              onClick={addToCart}
              disabled={currentStock <= 0}
              className="flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] text-sm font-bold text-white shadow-sm transition hover:brightness-110 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50"
            >
              <ShoppingCart className="h-4 w-4" />
              Add to Cart
            </button>
            {currentStock > 0 ? (
              <Link
                href="/checkout"
                onClick={() => addItem(product, quantity, selectedVariant)}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-[#FF8A00] text-sm font-bold text-white shadow-sm transition hover:bg-[#e67c00] active:scale-[0.99]"
              >
                <Zap className="h-4 w-4" />
                Buy Now
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-200 text-sm font-bold text-slate-400 shadow-sm dark:bg-slate-800 dark:text-slate-600 disabled:pointer-events-none"
              >
                <Zap className="h-4 w-4" />
                Unavailable
              </button>
            )}
          </>
        )}
        <button
          type="button"
          onClick={() => {
            toggleWishlist(product);
            toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist");
          }}
          className="flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 text-sm font-bold text-[#073B70] transition hover:border-[#087DB5] hover:bg-slate-50 dark:border-white/20 dark:text-slate-100 dark:hover:bg-white/5"
        >
          <Heart className={`h-4 w-4 ${safeIsWishlisted ? "fill-red-500 text-red-500" : ""}`} />
          {safeIsWishlisted ? "In wishlist" : "Add to Wish List"}
        </button>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-slate-50 py-2 px-3 text-xs text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
        <Lock className="h-3.5 w-3.5 text-[#00B7B0]" />
        <span>
          {isCustomerListing
            ? "Free Community Listing · Direct seller interaction"
            : "Secure transaction & buyer protection"}
        </span>
      </div>

      {/* Sticky Mobile Bottom Buy Bar: Always accessible on mobile phones without scrolling */}
      {compact && (
        <aside
          aria-label="Mobile purchase bar"
          className="fixed bottom-0 inset-x-0 z-50 flex items-center gap-2 border-t border-slate-200/90 bg-white/95 px-3 py-2.5 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] backdrop-blur-lg sm:hidden dark:border-white/10 dark:bg-slate-900/95 pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))]"
        >
          <div className="flex flex-col min-w-0 pr-1">
            <span className="truncate text-base font-bold text-[#073B70] dark:text-white leading-tight">
              {formatPrice(currentPrice)}
            </span>
            <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 truncate">
              {isCustomerListing ? "Direct Seller Post" : currentStock > 0 ? "In stock" : "Out of stock"}
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              toggleWishlist(product);
              toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist");
            }}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition active:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            aria-label="Wishlist"
          >
            <Heart className={`h-4 w-4 ${safeIsWishlisted ? "fill-red-500 text-red-500" : ""}`} />
          </button>

          {isCustomerListing ? (
            product.sellerPhone && (
              <a
                href={`tel:${product.sellerPhone}`}
                className="flex-1 h-10 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-3 text-xs font-bold text-white shadow-xs transition hover:bg-emerald-700 active:scale-95 flex"
                aria-label="Call seller"
              >
                <PhoneCall className="h-4 w-4" />
                <span>Call Seller ({product.sellerPhone})</span>
              </a>
            )
          ) : (
            <>
              <button
                type="button"
                onClick={addToCart}
                disabled={currentStock <= 0}
                className="flex-1 h-10 rounded-xl bg-gradient-to-r from-[#087DB5] to-[#073B70] px-2 text-xs font-bold text-white shadow-xs transition hover:brightness-110 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                <span>Cart</span>
              </button>

              {currentStock > 0 && (
                <Link
                  href="/checkout"
                  onClick={() => addItem(product, quantity, selectedVariant)}
                  className="flex-1 h-10 rounded-xl bg-[#FF8A00] px-2 text-xs font-bold text-white shadow-xs transition hover:bg-[#e67c00] active:scale-95 flex items-center justify-center gap-1"
                >
                  <Zap className="h-3.5 w-3.5" />
                  <span>Buy Now</span>
                </Link>
              )}
            </>
          )}
        </aside>
      )}
    </div>
  );
}
